import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Gamepad2, Search, X, Filter, BookOpenCheck, Clock, Check } from 'lucide-react';
import { Card, CardContent, cn, MathText } from '@jeraisonne/ui';
import { SCHOOL_LEVELS, SUBJECTS } from '@jeraisonne/db';
import type { SchoolLevel, SubjectSlug, ExerciseDifficulty } from '@jeraisonne/db';
import { useAuth } from '@/lib/auth';
import { useAdultDiagnosticState } from '@/lib/skill-diagnostic';
import { readStoredPlayerLevel, revisionLevel } from '@/lib/player/overview';
import {
  completedExerciseIds,
  usePlayerCompletedAttempts,
  usePlayerExercises,
  type PlayerExerciseRow,
} from '@/lib/player/queries';
import { filterExercisesForPlayer } from '@/lib/player/search-exercises';

const SUBJECT_LABEL = Object.fromEntries(SUBJECTS.map((s) => [s.value, s.label]));
const LEVEL_LABEL = Object.fromEntries(SCHOOL_LEVELS.map((l) => [l.value, l.label]));

const DIFFICULTY_META = {
  easy: { label: 'Facile', cls: 'bg-xp-50 text-xp-600 border-xp-100' },
  medium: { label: 'Moyen', cls: 'bg-level-50 text-level-600 border-level-100' },
  hard: { label: 'Difficile', cls: 'bg-accent-50 text-accent-600 border-accent-100' },
} as const;

type ExerciseRow = PlayerExerciseRow;

export function PlayerJouerPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const playerId = user?.id ?? '';
  const [search, setSearch] = useState('');
  const [filterSubject, setFilterSubject] = useState<SubjectSlug | ''>('');
  // Niveau : par défaut celui que le Curieux révise (bilan / choix mémorisé) ;
  // `null` = pas encore touché par l'utilisateur.
  const diag = useAdultDiagnosticState(playerId);
  const [levelChoice, setFilterLevel] = useState<SchoolLevel | '' | null>(null);
  const filterLevel: SchoolLevel | '' =
    levelChoice ??
    revisionLevel({
      stored: readStoredPlayerLevel(),
      inProgressLevel: diag.data?.in_progress?.level,
      lastCompletedLevel: diag.data?.last_completed?.level,
    }) ??
    '';
  const done = completedExerciseIds(usePlayerCompletedAttempts(playerId).data);
  const [filterDifficulty, setFilterDifficulty] = useState<ExerciseDifficulty | ''>('');
  const [showFilters, setShowFilters] = useState(false);

  const { data: exercises, isLoading } = usePlayerExercises({
    subject: filterSubject,
    level: filterLevel,
    difficulty: filterDifficulty,
  });

  // Le filtre matière/niveau est aussi appliqué côté client en complément de
  // la requête, pour rester robuste si la jointure renvoie des lignes hors filtre.
  const filtered = useMemo(
    () =>
      filterExercisesForPlayer(exercises ?? [], {
        search,
        subject: filterSubject,
        level: filterLevel,
        difficulty: filterDifficulty,
      }),
    [exercises, search, filterSubject, filterLevel, filterDifficulty],
  );

  function goToExercise(ex: ExerciseRow) {
    if (!ex.chapters) return;
    navigate({
      to: '/learn/$subject/$level/$chapter/$exercise',
      params: {
        subject: ex.chapters.subject,
        level: ex.chapters.level,
        chapter: ex.chapters.slug,
        exercise: ex.id,
      },
    });
  }

  return (
    <div className="mx-auto max-w-4xl space-y-5 px-4 py-6 pb-24 sm:py-8 md:p-8">
      {/* Header */}
      <div className="flex items-center gap-2">
        <Gamepad2 className="text-primary-600 h-6 w-6" />
        <h1 className="font-display text-ink-900 text-2xl font-extrabold">JeJoue</h1>
      </div>
      <p className="text-ink-500 -mt-3 font-sans text-sm">
        Cherche un exercice parmi notre catalogue public et lance-toi.
      </p>

      {/* Search bar */}
      <div className="relative">
        <Search className="text-ink-400 absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Rechercher un exercice ou un chapitre…"
          className="border-ink-200 text-ink-900 placeholder:text-ink-300 focus:ring-primary-400 h-12 w-full rounded-2xl border bg-white pl-10 pr-10 font-sans text-sm focus:outline-none focus:ring-2"
        />
        {search && (
          <button
            type="button"
            className="text-ink-400 hover:text-ink-600 absolute right-3 top-1/2 -translate-y-1/2"
            onClick={() => setSearch('')}
            aria-label="Effacer"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Filtres : bouton + puces des filtres actifs (chacune se retire d'un toucher) */}
      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          aria-expanded={showFilters}
          onClick={() => setShowFilters((v) => !v)}
          className={cn(
            'font-display inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-full border px-4 text-sm font-bold transition-colors active:scale-[0.98]',
            showFilters
              ? 'bg-primary-50 text-primary-700 border-primary-200'
              : 'text-ink-700 border-ink-200 hover:border-ink-300 bg-white',
          )}
        >
          <Filter className="h-4 w-4" aria-hidden /> Filtrer
        </button>
        {filterLevel && (
          <FilterChip
            label={`${LEVEL_LABEL[filterLevel] ?? filterLevel}${levelChoice === null ? ' · ton niveau' : ''}`}
            onClear={() => setFilterLevel('')}
          />
        )}
        {filterSubject && (
          <FilterChip
            label={SUBJECT_LABEL[filterSubject] ?? filterSubject}
            onClear={() => setFilterSubject('')}
          />
        )}
        {filterDifficulty && (
          <FilterChip
            label={DIFFICULTY_META[filterDifficulty].label}
            onClear={() => setFilterDifficulty('')}
          />
        )}
      </div>

      {showFilters && (
        <div className="border-ink-200 grid grid-cols-1 gap-3 rounded-2xl border bg-white p-4 sm:grid-cols-3">
          <div>
            <label className="font-display text-ink-600 mb-1.5 block text-xs font-semibold">
              Matière
            </label>
            <select
              value={filterSubject}
              onChange={(e) => setFilterSubject(e.target.value as SubjectSlug | '')}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-10 w-full appearance-none rounded-xl border px-3 font-sans text-sm focus:outline-none"
            >
              <option value="">Toutes</option>
              {SUBJECTS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.emoji} {s.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-display text-ink-600 mb-1.5 block text-xs font-semibold">
              Niveau
            </label>
            <select
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value as SchoolLevel | '')}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-10 w-full appearance-none rounded-xl border px-3 font-sans text-sm focus:outline-none"
            >
              <option value="">Tous</option>
              {SCHOOL_LEVELS.map((l) => (
                <option key={l.value} value={l.value}>
                  {l.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="font-display text-ink-600 mb-1.5 block text-xs font-semibold">
              Difficulté
            </label>
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value as ExerciseDifficulty | '')}
              className="border-ink-200 bg-ink-50 text-ink-900 focus:border-primary-500 h-10 w-full appearance-none rounded-xl border px-3 font-sans text-sm focus:outline-none"
            >
              <option value="">Toutes</option>
              <option value="easy">Facile</option>
              <option value="medium">Moyen</option>
              <option value="hard">Difficile</option>
            </select>
          </div>
        </div>
      )}

      {/* Results */}
      <div>
        <p className="text-ink-400 mb-3 font-sans text-xs">
          {isLoading
            ? 'Chargement…'
            : `${filtered.length} exercice${filtered.length !== 1 ? 's' : ''}`}
        </p>

        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="bg-ink-100 h-24 animate-pulse rounded-2xl" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="border-ink-100">
            <CardContent className="py-10 text-center">
              <BookOpenCheck className="text-ink-300 mx-auto mb-2 h-8 w-8" />
              <p className="text-ink-500 text-sm font-medium">
                Aucun exercice ne correspond à ta recherche.
              </p>
              <p className="text-ink-400 mt-1 text-xs">
                Essaie d'élargir tes filtres ou de changer de mot-clé.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {filtered.map((ex) => (
              <ExerciseCard
                key={ex.id}
                ex={ex}
                done={done.has(ex.id)}
                onClick={() => goToExercise(ex)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function FilterChip({ label, onClear }: { label: string; onClear: () => void }) {
  return (
    <button
      type="button"
      onClick={onClear}
      aria-label={`Retirer le filtre ${label}`}
      className="font-display bg-primary-50 text-primary-700 border-primary-200 ocean:bg-primary-500/15 inline-flex min-h-[2.75rem] items-center gap-1.5 rounded-full border px-4 text-sm font-bold active:scale-[0.98]"
    >
      {label}
      <X className="h-4 w-4" aria-hidden />
    </button>
  );
}

function ExerciseCard({
  ex,
  done,
  onClick,
}: {
  ex: ExerciseRow;
  done: boolean;
  onClick: () => void;
}) {
  const diff = DIFFICULTY_META[ex.difficulty];
  const ch = ex.chapters;
  return (
    <button
      type="button"
      onClick={onClick}
      className="border-ink-200 hover:border-primary-200 block rounded-2xl border bg-white p-4 text-left transition-all hover:shadow-md active:scale-[.98]"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          className={cn(
            'font-display inline-flex h-5 rounded-full border px-2 text-[10px] font-bold uppercase tracking-wide',
            diff.cls,
          )}
        >
          {diff.label}
        </span>
        {ch?.subject && (
          <span className="font-display text-ink-500 bg-ink-50 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
            {SUBJECT_LABEL[ch.subject] ?? ch.subject}
          </span>
        )}
        {ch?.level && (
          <span className="font-display text-ink-500 bg-ink-50 inline-flex h-5 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
            {LEVEL_LABEL[ch.level] ?? ch.level}
          </span>
        )}
        {done && (
          <span className="font-display bg-success-50 text-success-700 ml-auto inline-flex h-5 items-center gap-1 rounded-full px-2 text-[10px] font-bold uppercase tracking-wide">
            <Check className="h-3 w-3" aria-hidden /> Déjà fait
          </span>
        )}
      </div>
      <h3 className="font-display text-ink-900 line-clamp-2 text-[15px] font-extrabold leading-tight">
        <MathText>{ex.title}</MathText>
      </h3>
      <div className="border-ink-100 mt-3 flex items-center justify-between border-t pt-3">
        <span className="text-ink-500 truncate font-sans text-xs">{ch?.title ?? ''}</span>
        {ex.estimated_minutes && (
          <span className="text-ink-500 flex items-center gap-1 font-mono text-xs">
            <Clock className="h-3 w-3" /> {ex.estimated_minutes} min
          </span>
        )}
      </div>
    </button>
  );
}

// Re-export for router import
export default PlayerJouerPage;
